import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import LoadingSpinner from "../../../../common/LoadingSpinner";
import { ActivityHistoryData } from "../../../../common/classes/ActivityHistoryData";
import { SonicDBEntryData } from "../../../../common/classes/SonicDBEntry";
import { SonicUserData } from "../../../../common/classes/SonicUser";
import { ImageSourceSetData } from "../../../../common/classes/ImageSourceSet";
import { SonicTermData } from "../../../../common/classes/SonicTerm";
import Form from "../../../../common/forms/Form";
import FormInput from "../../../../common/forms/FormInput";
import Status from "../../../../common/management/detail/Status";
import Verification from "../../../../common/management/detail/Verification";
import ActionBtn from "../../../../common/management/ActionBtn";
import SaveButton from "../../../../common/nav/SaveButton";
import NotificationModal from "../../../../common/settings-modal/NotificationModal";

import { ValType } from "../../../../utils/ValidationFunctions";
import {
  genNotificationColor,
  RequestState,
  genNotificationMessage,
} from "../../../../utils/RequestState";
import { TermType } from "../../../../utils/useTerm";

import workStyles from "../../../../features/mypage/user_settings/database_entries/EditWork.module.scss";
import { SonicWorkInputData } from "../../../mypage/user_settings/database_entries/SonicWorkInputData";
import EditWorkItem from "../../../mypage/user_settings/database_entries/EditWorkItem";
import EditWorkModal from "../../../mypage/user_settings/database_entries/EditWorkModal";

import styles from "./DatabaseDetail.module.scss";
import SelectTerm from "../../../../common/forms/SelectTerm";
import SelectMultipleTerms from "../../../../common/forms/SelectMultipleTerms";
import SelenaA from "../../../../assets/img/service/selena-a.webp";
import { SonicGameSelectionData } from "../../../../common/classes/SonicGameSelection";
import { SonicGameDetailData } from "../../../../common/classes/SonicGameDetail";
import EditGameDetail from "../../../mypage/user_settings/database_entries/EditGameDetail";
import SonicLink from "../../../../common/routing/SonicLink";

type Props = {
  error: boolean;
  person: SonicDBEntryData;
  owner: SonicUserData;
  image?: File | ImageSourceSetData;
  deleteImage: () => void;
  isLoading: boolean;
  selectStatus?: string;
  setSelectStatus: (value: string, name: string) => void;
  handleUpdate: () => void;
  handleInputChange: (
    value: File | string | boolean | undefined,
    name: string
  ) => void;
  typeOptions?: Array<[string, string]>;
  games?: Array<SonicTermData>;
  handleGames: (value: Array<SonicTermData>) => void;
  team?: SonicTermData;
  handleTeam: (value: SonicTermData) => void;
  showStatusMenu: boolean;
  handleStatusMenu: () => void;
  historyItems: Array<ActivityHistoryData>;
  handleHistoryInputChange: (
    index: number,
    prop: string,
    value?: string
  ) => void;
  verified: boolean;
  setVerified: (value: boolean) => void;
  requestState: RequestState;
  setRequestState: (value: RequestState) => void;

  // works
  workItems?: Array<SonicWorkInputData>;
  isEditWork: boolean;
  editWorkNumber: number;
  addWork: () => void;
  deleteWork: (i: number) => void;
  editWork: (i: number, work: SonicWorkInputData) => void;
  setIsEditWork: (value: boolean) => void;
  setEditWorkNumber: (i: number) => void;

  // game details
  gameDetailLoading: boolean;
  gameDetailOptions?: Array<SonicGameSelectionData>;
  handleGameDetails: (details: Array<SonicGameDetailData>) => void;
};

const PersonDetail: React.FC<Props> = ({
  error,
  person,
  owner,
  image,
  deleteImage,
  isLoading,
  selectStatus,
  setSelectStatus,
  handleUpdate,
  handleInputChange,
  typeOptions,
  games,
  handleGames,
  team,
  handleTeam,
  showStatusMenu,
  handleStatusMenu,
  historyItems,
  handleHistoryInputChange,
  verified,
  setVerified,
  requestState,
  setRequestState,
  workItems,
  isEditWork,
  editWorkNumber,
  addWork,
  deleteWork,
  editWork,
  setIsEditWork,
  setEditWorkNumber,
  gameDetailLoading,
  gameDetailOptions,
  handleGameDetails,
}) => {
  return (
    <div className={styles["f-databaseDetail"]}>
      {isLoading || !person || !owner ? (
        <LoadingSpinner space={1} />
      ) : (
        <>
          <h1 className={styles["f-databaseDetail__title"]}>
            プロフィール詳細
          </h1>
          <div className={styles["f-databaseDetail__inner"]}>
            <div className={styles["f-databaseDetail__head"]}>
              <Status
                isVisibilityStatus={true}
                status={person.status}
                selectStatus={selectStatus}
                showStatusMenu={showStatusMenu}
                handleStatusMenu={handleStatusMenu}
                setSelectStatus={setSelectStatus}
              />
              <div className={styles["f-databaseDetail__user"]}>
                <div className={styles["f-databaseDetail__user-head"]}>
                  <h2 className={styles["f-databaseDetail__user-head__title"]}>
                    ユーザー情報
                  </h2>
                  <SonicLink to={`/admin/member/${owner?.id}`}>
                    ユーザー詳細
                  </SonicLink>
                </div>
                <div className={styles["f-databaseDetail__user-info"]}>
                  <div>
                    <p>ユーザー名</p>
                    <span>{owner.meta.name}</span>
                  </div>
                  <div>
                    <p>メールアドレス</p>
                    <span>{owner.meta.email}</span>
                  </div>
                </div>
              </div>
              <Verification
                verified={verified}
                setVerified={setVerified}
                setRequestState={setRequestState}
              />
            </div>
            <Form handler={() => {}}>
              <div
                className={`${styles["f-databaseDetail__form"]} ${styles["vertical"]} ${styles["error-space"]}`}
              >
                <label className={styles["f-databaseDetail__form-title"]}>
                  プロフィール画像
                </label>
                {image ? (
                  <>
                    <div className={styles["imageBox"]}>
                      <img
                        src={
                          image instanceof File
                            ? URL.createObjectURL(image)
                            : image.medium?.url
                        }
                      />
                    </div>
                    <button
                      type="button"
                      onClick={deleteImage}
                      className={`${styles["f-databaseDetail__form-fileBtn"]} ${styles["delete"]}`}
                    >
                      <FontAwesomeIcon icon={["fal", "trash-alt"]} />
                      削除する
                    </button>
                  </>
                ) : null}
                {image == undefined ? (
                  <button
                    type="button"
                    className={`${styles["f-databaseDetail__form-fileBtn"]} ${styles["add"]}`}
                  >
                    <FontAwesomeIcon icon={["fal", "plus-circle"]} />
                    <FormInput
                      type="file"
                      label="追加する"
                      id="imgs"
                      name="sonic_imgs"
                      handler={handleInputChange}
                      val_type={ValType.File}
                    />
                  </button>
                ) : null}
              </div>
              <div
                className={`${styles["f-databaseDetail__form"]} ${styles["error-space"]}`}
              >
                <FormInput
                  id="name"
                  type="text"
                  label="プロフィール名"
                  label_class={`${styles["f-databaseDetail__form-title"]} ${styles["required-mark"]}`}
                  name="sonic_name"
                  style_class={styles["f-databaseDetail__form-input"]}
                  handler={handleInputChange}
                  initial_value={person.name}
                  val_type={ValType.NotNull}
                />
              </div>
              <div className={styles["f-databaseDetail__form"]}>
                <FormInput
                  type="text"
                  label="ふりがな"
                  label_class={styles["f-databaseDetail__form-title"]}
                  name="sonic_kana"
                  style_class={styles["f-databaseDetail__form-input"]}
                  handler={handleInputChange}
                  initial_value={person.kana}
                />
              </div>
              <SelectTerm
                termType={TermType.Teams}
                termName="チーム"
                placeholder="未選択"
                term={team}
                handleTerm={handleTeam}
                isAdminPage={true}
              />
              <div className={styles["f-databaseDetail__form"]}>
                <FormInput
                  type="select"
                  options={typeOptions}
                  label="タイプ"
                  label_class={styles["f-databaseDetail__form-title"]}
                  name="sonic_player_type"
                  select_class={styles["f-databaseDetail__form-input"]}
                  holder_class={styles["f-databaseDetail__form-input"]}
                  handler={handleInputChange}
                  initial_value={person.player_type?.[0]?.id}
                  placeholder="未選択"
                />
              </div>
              <div className={styles["f-databaseDetail__form"]}>
                <div className={styles["f-databaseDetail__form-title"]}>
                  マイゲーム
                </div>
                <div className={styles["f-databaseDetail__form-box"]}>
                  <EditGameDetail
                    isManagement={true}
                    initialData={person.game_details}
                    games={games}
                    isLoading={gameDetailLoading}
                    gameSelectionOptions={
                      gameDetailOptions ? gameDetailOptions : []
                    }
                    handleGameDetails={handleGameDetails}
                  />
                  <SelectMultipleTerms
                    termType={TermType.Games}
                    termName={"ゲーム"}
                    buttonText={"マイゲームを選ぶ"}
                    terms={games ? games : []}
                    handleTerms={handleGames}
                    isAdminPage={true}
                  />
                </div>
              </div>
              <div className={styles["f-databaseDetail__form"]}>
                <FormInput
                  type="textarea"
                  label="プロフィール"
                  label_class={styles["f-databaseDetail__form-title"]}
                  name="sonic_profile"
                  style_class={styles["f-databaseDetail__form-input"]}
                  handler={handleInputChange}
                  initial_value={person.profile}
                  rows={3}
                />
              </div>
              <div className={styles["f-databaseDetail__form"]}>
                <FormInput
                  id="birthplace"
                  type="text"
                  label="出身地"
                  label_class={styles["f-databaseDetail__form-title"]}
                  name="sonic_birthplace"
                  style_class={styles["f-databaseDetail__form-input"]}
                  handler={handleInputChange}
                  initial_value={person.birthplace}
                />
              </div>
              <div className={styles["f-databaseDetail__form"]}>
                <FormInput
                  type="text"
                  label="身長"
                  label_class={styles["f-databaseDetail__form-title"]}
                  name="sonic_height"
                  style_class={styles["f-databaseDetail__form-input"]}
                  handler={handleInputChange}
                  initial_value={person.height}
                />
              </div>
              <div className={styles["f-databaseDetail__form"]}>
                <FormInput
                  type="text"
                  label="体重"
                  label_class={styles["f-databaseDetail__form-title"]}
                  name="sonic_weight"
                  style_class={styles["f-databaseDetail__form-input"]}
                  handler={handleInputChange}
                  initial_value={person.weight}
                />
              </div>
              <div className={styles["f-databaseDetail__form"]}>
                <FormInput
                  type="textarea"
                  label="趣味"
                  label_class={styles["f-databaseDetail__form-title"]}
                  name="sonic_hobby"
                  style_class={styles["f-databaseDetail__form-input"]}
                  handler={handleInputChange}
                  initial_value={person.hobby}
                  rows={3}
                />
              </div>
              <div
                className={`${styles["f-databaseDetail__form"]} ${styles["error-space"]}`}
              >
                <FormInput
                  id="twitter"
                  type="text"
                  label="X (旧Twitter)"
                  label_class={styles["f-databaseDetail__form-title"]}
                  name="sonic_twitter"
                  style_class={styles["f-databaseDetail__form-input"]}
                  handler={handleInputChange}
                  initial_value={person.sns.twitter?.toString()}
                  val_type={ValType.Twitter}
                />
              </div>
              <div
                className={`${styles["f-databaseDetail__form"]} ${styles["error-space"]}`}
              >
                <FormInput
                  type="text"
                  label="Instagram"
                  label_class={styles["f-databaseDetail__form-title"]}
                  name="sonic_instagram"
                  style_class={styles["f-databaseDetail__form-input"]}
                  handler={handleInputChange}
                  initial_value={person.sns.instagram?.toString()}
                  val_type={ValType.Instagram}
                />
              </div>
              <div
                className={`${styles["f-databaseDetail__form"]} ${styles["error-space"]}`}
              >
                <FormInput
                  type="text"
                  label="Facebook"
                  label_class={styles["f-databaseDetail__form-title"]}
                  name="sonic_facebook"
                  style_class={styles["f-databaseDetail__form-input"]}
                  handler={handleInputChange}
                  initial_value={person.sns.facebook?.toString()}
                  val_type={ValType.Facebook}
                />
              </div>
              <div
                className={`${styles["f-databaseDetail__form"]} ${styles["error-space"]}`}
              >
                <FormInput
                  type="text"
                  label="Youtube"
                  label_class={styles["f-databaseDetail__form-title"]}
                  name="sonic_youtube"
                  style_class={styles["f-databaseDetail__form-input"]}
                  handler={handleInputChange}
                  initial_value={
                    person.sns.youtube
                      ? person.sns.youtube.toString()
                      : "https://youtube.com/channel/"
                  }
                  val_type={ValType.Youtube}
                />
              </div>
              <div
                className={`${styles["f-databaseDetail__form"]} ${styles["error-space"]}`}
              >
                <FormInput
                  type="text"
                  label="Twitch"
                  label_class={styles["f-databaseDetail__form-title"]}
                  name="sonic_twitch"
                  style_class={styles["f-databaseDetail__form-input"]}
                  handler={handleInputChange}
                  initial_value={person.sns.twitch?.toString()}
                  val_type={ValType.Twitch}
                />
              </div>
              <div className={styles["f-databaseDetail__form"]}>
                <span className={styles["f-databaseDetail__form-title"]}>
                  活動実績
                </span>
                <div className={styles["f-databaseDetail__form-history"]}>
                  {historyItems.map((item, i) => (
                    <div
                      key={i}
                      className={styles["f-databaseDetail__form-history__item"]}
                    >
                      <FormInput
                        type="text"
                        label=""
                        id=""
                        name={"year_" + i}
                        label_class={
                          styles["f-databaseDetail__form-history__item-label"]
                        }
                        style_class={`${styles["f-databaseDetail__form-input"]} ${styles["year"]}`}
                        initial_value={item.year}
                        handler={(value: string) => {
                          handleHistoryInputChange(i, "year", value);
                        }}
                      />
                      <span>年</span>
                      <FormInput
                        type="text"
                        label=""
                        id=""
                        name={"title_" + i}
                        style_class={styles["f-databaseDetail__form-input"]}
                        initial_value={item.title}
                        handler={(value: string) => {
                          handleHistoryInputChange(i, "title", value);
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div
                className={`${styles["f-databaseDetail__form"]} ${styles["vertical"]}`}
              >
                <div className={styles["f-databaseDetail__form-title"]}>
                  作品
                </div>
                <div className={workStyles["edit-work"]}>
                  {workItems?.map((work, i) => (
                    <div
                      key={i}
                      className={workStyles["edit-workItem-wrapper"]}
                    >
                      <EditWorkItem
                        index={i}
                        title={work.title}
                        img_src={work.image}
                        link={work.link}
                        setIsEditWork={setIsEditWork}
                        setEditWorkNumber={setEditWorkNumber}
                        deleteWork={deleteWork}
                      />
                    </div>
                  ))}
                </div>
                <div className={workStyles["add-work"]}>
                  <SaveButton title="作品を追加する" onClick={addWork} />
                </div>
              </div>

              {isEditWork ? (
                <EditWorkModal
                  index={editWorkNumber}
                  image={workItems?.[editWorkNumber]?.image}
                  title={workItems?.[editWorkNumber]?.title}
                  link={workItems?.[editWorkNumber]?.link}
                  description={workItems?.[editWorkNumber]?.description}
                  editWork={editWork}
                  closeHandler={() => setIsEditWork(false)}
                />
              ) : null}
            </Form>
            {error ? (
              <div className={styles["f-databaseDetail__error"]}>
                必須項目が未入力、または入力内容に誤りがあります
              </div>
            ) : null}
            <div className={styles["f-databaseDetail__bottom"]}>
              <ActionBtn
                label="保存"
                onClick={() => handleUpdate()}
                colorReversal={true}
              />
            </div>
            <div className={styles["f-databaseDetail__btn"]}>
              <ActionBtn
                label=" 保存 "
                onClick={() => handleUpdate()}
                colorReversal={true}
              />
            </div>
          </div>
        </>
      )}
      {requestState !== RequestState.Waiting ? (
        <NotificationModal
          color={genNotificationColor(requestState)}
          message={genNotificationMessage(requestState)}
          img_src={requestState === RequestState.Success ? SelenaA : undefined}
        />
      ) : null}
    </div>
  );
};

export default PersonDetail;
